import { useEffect, useRef, useState, type DragEvent } from 'react';
import { FileArrowUp } from '@phosphor-icons/react';
import { dragLeftWindow, isDesktopFileDrag } from '~/lib/file-drag';

/**
 * FRONT-3138: the Build window takes a file drop anywhere on itself, not just
 * on the composer box, and hands it to the composer's own upload path.
 *
 * "Supported" is a real file from the desktop: the Builder stages any type
 * into the project's `uploads/` folder (src/coding/uploads.ts), so the type
 * is not the gate — the drag's kind is. A dragged text selection, link or an
 * in-page card carries no `'Files'` entry and never lights the overlay.
 * There must also be a composer to receive it — an open build's, or the
 * new-project screen's, whose files become the project's references (none for
 * a person without the builder role, on a load error, or before the session
 * arrives).
 */
export function acceptsBuildFileDrop(types: readonly string[] | undefined | null, composerShown: boolean): boolean {
  return composerShown && isDesktopFileDrag(types);
}

/**
 * A window-level drag event that means the overlay must close, whatever the
 * depth count says. The count drifts whenever a `dragleave` goes missing —
 * the element under the pointer re-rendered or unmounted mid-drag, the drag
 * was cancelled with Escape, or it ended outside the browser — and a drifted
 * count leaves "Drop to attach" up with nothing being dragged. So the drag
 * also settles on the window: it ended (`dragend`/`drop`), it left the
 * browser window, or it is now hovering a part of the page outside the Build
 * window (a docked Build sits beside the chat, where a stale overlay used to
 * stay lit).
 */
export function buildDropSettles(
  e: { type: string; target?: unknown; relatedTarget?: unknown; clientX?: number; clientY?: number },
  root: { contains: (node: Node | null) => boolean } | null,
  viewport: { width: number; height: number },
): boolean {
  if (e.type === 'dragend' || e.type === 'drop') return true;
  if (e.type === 'dragleave') return dragLeftWindow(e, viewport);
  if (e.type === 'dragover') return root !== null && !root.contains((e.target ?? null) as Node | null);
  return false;
}

/**
 * How long the page may go without a `dragover` before a drag counts as over.
 * While a file hovers the page the browser repeats `dragover` every few
 * hundred milliseconds even when the pointer is still (the HTML drag model
 * runs every 350ms ± 200ms), so a second of silence means the drag ended in a
 * way no event announced.
 */
export const DRAG_IDLE_MS = 1000;

/**
 * The overlay's state, without React or the DOM so every ending can be
 * replayed in a test. A depth count, because dragenter/dragleave fire for
 * every child the pointer crosses; plus an idle timer re-armed by every
 * `dragover`. The timer is the backstop for the endings that send nothing the
 * count can see (FRONT-3138): Escape on a desktop file drag sends one
 * `dragleave` with the pointer still inside the window and never a `drop` or
 * `dragend` (the drag's source is outside the browser), and a fast exit can
 * report a last position just inside the edge. Both left "Drop to attach" up.
 */
export function createBuildDropTracker({
  onActive,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  idleMs = DRAG_IDLE_MS,
}: {
  onActive: (active: boolean) => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
  idleMs?: number;
}) {
  let depth = 0;
  let timer: unknown = null;
  const disarm = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const settle = () => {
    disarm();
    if (depth === 0) return;
    depth = 0;
    onActive(false);
  };
  const arm = () => {
    disarm();
    timer = setTimer(settle, idleMs);
  };
  return {
    get depth() {
      return depth;
    },
    enter() {
      depth += 1;
      onActive(true);
      arm();
    },
    /** A `dragover` on the window: the drag is still alive. */
    over() {
      if (depth > 0) arm();
    },
    leave() {
      if (depth === 1) settle();
      else if (depth > 1) depth -= 1;
    },
    settle,
  };
}

/**
 * Handlers for the whole window plus whether the overlay shows.
 *
 * The composer box keeps its own drop handler, which calls `preventDefault` —
 * a drop it already took arrives here with `defaultPrevented` set and only
 * resets the overlay, or the file would be uploaded twice. Endings the
 * handlers miss are caught at window level (`buildDropSettles`) and, for the
 * ones no event announces, by the tracker's idle timer.
 */
export function useBuildFileDrop({ composerShown, onFiles }: { composerShown: boolean; onFiles: (files: File[]) => void }) {
  const root = useRef<HTMLElement | null>(null);
  const [active, setActive] = useState(false);
  const tracker = useRef<ReturnType<typeof createBuildDropTracker> | null>(null);
  if (!tracker.current) tracker.current = createBuildDropTracker({ onActive: setActive });
  useEffect(() => {
    const t = tracker.current!;
    const onWindow = (e: globalThis.DragEvent) => {
      if (t.depth === 0) return;
      if (buildDropSettles(e, root.current, { width: window.innerWidth, height: window.innerHeight })) t.settle();
      else if (e.type === 'dragover') t.over();
    };
    const types = ['dragend', 'drop', 'dragleave', 'dragover'] as const;
    for (const type of types) window.addEventListener(type, onWindow, true);
    return () => {
      for (const type of types) window.removeEventListener(type, onWindow, true);
      t.settle();
    };
  }, []);
  const accepts = (e: DragEvent) => acceptsBuildFileDrop(Array.from(e.dataTransfer?.types ?? []), composerShown);
  return {
    active: active && composerShown,
    handlers: {
      onDragEnter: (e: DragEvent) => {
        if (!accepts(e)) return;
        e.preventDefault();
        root.current = e.currentTarget as HTMLElement;
        tracker.current!.enter();
      },
      onDragOver: (e: DragEvent) => {
        if (!accepts(e)) return;
        // Without this the browser refuses the drop — and on the backdrop it
        // would open the file in the tab, navigating away from Eddy.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      },
      onDragLeave: (e: DragEvent) => {
        if (!accepts(e)) return;
        tracker.current!.leave();
      },
      onDrop: (e: DragEvent) => {
        if (!accepts(e)) return;
        const takenByComposer = e.defaultPrevented;
        e.preventDefault();
        tracker.current!.settle();
        if (takenByComposer) return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) onFiles(files);
      },
    },
  };
}

/** The overlay over the window while a file hovers it. Decorative to pointers
 *  (the drop lands on the window underneath); the words are announced. */
export function BuildFileDropOverlay() {
  return (
    <div
      data-testid="build-file-drop-overlay"
      role="status"
      className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center p-3"
      style={{ animation: 'eddy-coding-bg-in 120ms ease-out' }}
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-[calc(var(--radius-xl-ds)-6px)] border-2 border-dashed border-primary/60 bg-card/85 backdrop-blur-sm">
        <FileArrowUp size={32} className="text-primary" aria-hidden />
        <span className="text-[length:var(--text-md)] font-semibold text-foreground">Drop to attach</span>
        <span className="text-[length:var(--text-sm)] text-muted-foreground">
          The files go with your next message into the project&apos;s uploads folder
        </span>
      </div>
    </div>
  );
}
